"use client";

import { createContext, useContext, useState, useCallback, ReactNode } from "react";

// Live region context for managing announcements
interface LiveRegionContextType {
  announce: (message: string, priority?: "polite" | "assertive") => void;
}

const LiveRegionContext = createContext<LiveRegionContextType | null>(null);

// Hook to use the live region from any component
export function useLiveRegion() {
  const context = useContext(LiveRegionContext);
  if (!context) {
    throw new Error("useLiveRegion must be used within a LiveRegionProvider");
  }
  return context;
}

// Provider component that should be placed in the app layout
interface LiveRegionProviderProps {
  children: ReactNode;
}

export function LiveRegionProvider({ children }: LiveRegionProviderProps) {
  const [politeMessage, setPoliteMessage] = useState("");
  const [assertiveMessage, setAssertiveMessage] = useState("");

  const announce = useCallback((message: string, priority: "polite" | "assertive" = "polite") => {
    if (priority === "assertive") {
      setAssertiveMessage(message);
      // Clear the message after a short delay so it can be announced again if needed
      setTimeout(() => setAssertiveMessage(""), 100);
    } else {
      setPoliteMessage(message);
      // Clear the message after a short delay so it can be announced again if needed
      setTimeout(() => setPoliteMessage(""), 100);
    }
  }, []);

  return (
    <LiveRegionContext.Provider value={{ announce }}>
      {children}
      {/* Screen reader only live regions */}
      <div className="sr-only">
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          id="live-region-polite"
        >
          {politeMessage}
        </div>
        <div
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
          id="live-region-assertive"
        >
          {assertiveMessage}
        </div>
      </div>
    </LiveRegionContext.Provider>
  );
}

// Utility hook for location-specific announcements
export function useLocationAnnouncements() {
  const { announce } = useLiveRegion();

  const announceLocationChange = useCallback((
    locationType: "community" | "beyond",
    locationName?: string,
    radiusKm?: number
  ) => {
    let message = "";
    
    if (locationType === "community") {
      const location = locationName || "your location";
      const radius = radiusKm || 30;
      message = `Location preference updated: Exploring your community around ${location} within ${radius} kilometers.`;
    } else {
      message = "Location preference updated: Exploring beyond - unlimited exploration mode activated.";
    }
    
    announce(message, "polite");
  }, [announce]);

  const announceLocationUpdate = useCallback((message: string) => {
    announce(message, "polite");
  }, [announce]);

  const announceLocationError = useCallback((message: string) => {
    announce(message, "assertive");
  }, [announce]);

  return {
    announceLocationChange,
    announceLocationUpdate, 
    announceLocationError,
  };
}